import React, { PureComponent } from 'react';

import { cn } from '../../utils';
import { Button } from '../Button';
import styles from './index.module.scss';
import { Text } from '../Typography';


type TabSliderSelectorOption = {
    tab: React.ReactNode;
    clickable: boolean;
    disabled?: boolean;
    title?: string;
};

export type TabSliderSelectorProps = {
    value: number;
    focusable?: boolean;
    options: TabSliderSelectorOption[];
    onChange?: (id: number) => void;

    classNames?: {
        container?: string;
        tab?: string;
        active?: string;
        positioner?: string;
        reverse?: string;
    };
};

type TabSliderSelectorState = {
    left: number;
    right: number;
    reverse: boolean;
    noAnimation: boolean;
};

export class TabSliderSelector extends PureComponent
    <TabSliderSelectorProps, TabSliderSelectorState> {
    state = {
        left: 0.5,
        right: 0.5,
        reverse: false,
        noAnimation: true,
    };

    container = React.createRef<HTMLDivElement>();

    elements: HTMLButtonElement[] = [];

    componentDidMount() {
        this.updatePositioner(this.props.options.length / 2, true);
    }

    componentDidUpdate({ value }: TabSliderSelectorProps) {
        this.updatePositioner(value);
    }

    updatePositioner = (previous: number, noAnimation: boolean = false) => setTimeout(() => {
        const { value } = this.props;
        if (!this.elements[value] || !this.container.current)
            return;

        const { offsetLeft, clientWidth } = this.elements[value];
        const { clientWidth: containerWidth } = this.container.current;
        const left = offsetLeft / containerWidth;
        const right = 1 - (offsetLeft + clientWidth) / containerWidth;
        this.setState({
            left,
            right,
            reverse: value < previous,
            noAnimation,
        });
    }, 10);

    setIndex = (idx: number) => () => {
        if (this.props.onChange)
            this.props.onChange(idx);
    };

    setElementRef = (idx: number) => (ref: HTMLButtonElement) => {
        this.elements[idx] = ref;
    }

    render() {
        const { classNames = {}, focusable = true, options } = this.props;
        const { left, right, reverse, noAnimation } = this.state;
        this.elements.length = options.length;
        return (
            <div
                ref={this.container}
                className={cn(styles.tabSliderSelector, classNames.container)}
            >
                <div
                    className={cn(
                        styles.positioner,
                        classNames.positioner,
                        reverse && styles.reverse,
                        reverse && classNames.reverse,
                        noAnimation && styles.noAnimation,
                    )}
                    style={{
                        left: `${left * 100}%`,
                        right: `${right * 100}%`,
                    }}
                />
                {options.map(({ tab, clickable, disabled, title = '' }, idx) => clickable ? (
                    <Text
                        key={idx}
                        componentRef={this.setElementRef(idx)}
                        component={Button}
                        buttonEllipsis
                        variant='auxiliary'
                        tabIndex={focusable ? 0 : -1}
                        disabled={disabled}
                        onClick={this.setIndex(idx)}
                        className={cn(styles.tab, styles.separate, classNames.tab, {
                            [styles.active]: idx === this.props.value,
                            [classNames.active || styles.active]: idx === this.props.value,
                        })}
                        title={title}
                    >
                        {tab}
                    </Text>
                ) : (
                    <span
                        key={idx}
                        ref={this.setElementRef(idx)}
                        className={cn(styles.tab, classNames.tab, {
                            [styles.active]: idx === this.props.value,
                            [classNames.active || styles.active]: idx === this.props.value,
                        })}
                    >
                        {tab}
                    </span>
                ))}
            </div>
        );
    }
}
